import React, { useEffect, useMemo, useState } from "react";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { format, isSameDay, isValid } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { FaVideo, FaHospital } from "react-icons/fa";
import useBDocTranslator from "../hooks/useBDocTranslator";
import useUserState from "../hooks/useUserState";
import Axios from "axios";

// Valor que usan los formularios para "sin cita vinculada".
export const NO_APPOINTMENT = "none";

const useStyles = makeStyles((theme) => ({
  option: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
    padding: theme.spacing(0.5, 0),
  },
  optionIcon: {
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    backgroundColor: "rgba(7, 108, 216, 0.08)",
  },
  optionBody: {
    flex: 1,
    minWidth: 0,
  },
  optionTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    textTransform: "capitalize",
  },
  optionMeta: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chip: {
    flexShrink: 0,
    height: 22,
    fontSize: "0.72rem",
  },
  group: {
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    lineHeight: "32px",
  },
}));

const toDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && isValid(date) ? date : null;
};

const toTime = (value) => toDate(value)?.getTime() || 0;

/**
 * Selector de cita para vincular una receta o nota de consulta.
 *
 * - Con `patientId` solo trae las citas de ese paciente.
 * - Ordena de la más reciente a la más antigua y agrupa en próximas/anteriores.
 * - Permite buscar por fecha, motivo, estado, código o paciente.
 * - `value` es el objeto de la cita o `NO_APPOINTMENT` ("none").
 */
const SelectAppt = ({
  onChange = () => {},
  value,
  patientId,
  label,
  helperText,
}) => {
  const classes = useStyles();
  const { t, language } = useBDocTranslator();
  const userLocalState = useUserState();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);

  const locale = language === "en" ? enUS : es;

  useEffect(() => {
    if (!userLocalState?.[0]?.extendedData?.id) return;
    const params = {
      provider_id: userLocalState[0].extendedData.id,
    };
    if (patientId) {
      params.patient_id = patientId;
    }

    let active = true;
    setLoading(true);
    Axios.get(`${process.env.REACT_APP_API_URL_BASE}/appointments/select`, {
      headers: {
        Authorization: "Bearer " + userLocalState[0].jwt,
      },
      params,
    })
      .then((res) => {
        if (active) setAppts(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Hoy primero (la cita que el médico suele buscar), luego las próximas
  // (la más cercana primero) y al final las anteriores (la más reciente
  // primero). groupBy necesita que cada grupo esté contiguo.
  const options = useMemo(() => {
    const now = new Date();
    const today = [];
    const upcoming = [];
    const past = [];
    appts.forEach((appt) => {
      const date = toDate(appt.appt_date_start);
      if (date && isSameDay(date, now)) today.push(appt);
      else if (date && date > now) upcoming.push(appt);
      else past.push(appt);
    });
    const asc = (a, b) => toTime(a.appt_date_start) - toTime(b.appt_date_start);
    const desc = (a, b) => asc(b, a);
    today.sort(asc);
    upcoming.sort(asc);
    past.sort(desc);
    return [
      ...today.map((a) => ({ ...a, __group: t("Today") })),
      ...upcoming.map((a) => ({ ...a, __group: t("Upcoming appointments") })),
      ...past.map((a) => ({ ...a, __group: t("Past appointments") })),
    ];
  }, [appts, t]);

  const formatDate = (appt) => {
    const date = toDate(appt?.appt_date_start);
    return date ? format(date, "EEE d MMM yyyy · HH:mm", { locale }) : "—";
  };

  const isVirtual = (appt) => appt?.event_type === "VIRTUAL";
  const modality = (appt) => (isVirtual(appt) ? t("Virtual") : t("On site"));

  const patientName = (appt) =>
    [appt?.patient?.firstName, appt?.patient?.lastname]
      .filter(Boolean)
      .join(" ");

  const metaLine = (appt) =>
    [
      !patientId && patientName(appt),
      modality(appt),
      appt?.appt_reason,
      appt?.event_code,
    ]
      .filter(Boolean)
      .join(" · ");

  const getOptionLabel = (appt) =>
    appt && typeof appt === "object"
      ? [formatDate(appt), !patientId && patientName(appt), modality(appt)]
          .filter(Boolean)
          .join(" · ")
      : "";

  const filterOptions = createFilterOptions({
    stringify: (appt) =>
      [
        formatDate(appt),
        appt?.appt_date_start,
        patientName(appt),
        appt?.appt_reason,
        appt?.event_code,
        appt?.status_appointment?.description &&
          t(appt.status_appointment.description),
        modality(appt),
      ]
        .filter(Boolean)
        .join(" "),
  });

  const selected =
    value && value !== NO_APPOINTMENT
      ? options.find((o) => o.id === value.id) || value
      : null;

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      loading={loading}
      groupBy={(appt) => appt.__group}
      getOptionLabel={getOptionLabel}
      getOptionSelected={(option, current) => option.id === current?.id}
      filterOptions={filterOptions}
      onChange={(event, appt) => onChange(appt || NO_APPOINTMENT)}
      noOptionsText={
        patientId
          ? t("This patient has no appointments")
          : t("No appointments found")
      }
      loadingText={t("Loading...")}
      classes={{ groupLabel: classes.group }}
      renderOption={(appt) => (
        <div className={classes.option}>
          <span className={classes.optionIcon}>
            {isVirtual(appt) ? <FaVideo size={14} /> : <FaHospital size={14} />}
          </span>
          <div className={classes.optionBody}>
            <div className={classes.optionTitle}>{formatDate(appt)}</div>
            <div className={classes.optionMeta}>{metaLine(appt)}</div>
          </div>
          {appt?.status_appointment?.description && (
            <Chip
              size="small"
              variant="outlined"
              className={classes.chip}
              label={t(appt.status_appointment.description)}
            />
          )}
        </div>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label || t("Linked appointment")}
          placeholder={t("No linked appointment")}
          helperText={
            helperText ||
            t("Optional. Search by date, reason or appointment code.")
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SelectAppt;
